/**
 * upgradePrompt.js - Pinia store for the global "feature requires upgrade" dialog.
 *
 * UX principle (2026-05-16): we never hide gated features. Users can see and
 * interact with the full surface. When they attempt a gated ACTION, this store's
 * `guard()` is the single intercept point that opens the upgrade dialog at the
 * moment of intent (not at 403 time, not by greying out controls).
 *
 * Usage from any page:
 *   import { useUpgradePromptStore } from '@stores/upgradePrompt.js';
 *
 *   methods: {
 *       async onSubmit() {
 *           if (!this.upgradePrompt.guard('allowBulkSend', 'Bulk Send', 'Submit bulk send')) return;
 *           await this.actuallySubmit();
 *       }
 *   },
 *   computed: {
 *       upgradePrompt() { return useUpgradePromptStore(); }
 *   }
 *
 * The dialog itself is mounted once in AppLayout.vue.
 */
import { defineStore } from 'pinia';

export const useUpgradePromptStore = defineStore('upgradePrompt', {
    state: () => ({
        open: false,
        featureLabel: '',
        actionContext: ''
    }),

    actions: {
        /**
         * Single intercept point for gated actions. Returns true if the feature
         * is enabled (caller should proceed) or false if not (caller should
         * abort; this method already opened the upgrade dialog).
         *
         * @param {string} featureKey - key in window._PLAN_FEATURES (e.g. 'allowBulkSend')
         * @param {string} featureLabel - human-readable feature name shown in the dialog
         * @param {string} actionContext - what the user was trying to do (e.g. "Submit this bulk send")
         * @returns {boolean} true if allowed, false if denied (dialog opened)
         */
        guard(featureKey, featureLabel, actionContext) {
            const features = window._PLAN_FEATURES || {};
            if (features[featureKey] === true) return true;
            this.featureLabel = featureLabel;
            this.actionContext = actionContext;
            this.open = true;
            return false;
        },

        close() {
            this.open = false;
        }
    }
});
